import { css } from "@emotion/react";
import { Link } from "react-router";
import { ArticleCard, type Post, type Tag } from "./ArticleCard";
import { FrjLogoWithTitle } from "./FrjLogoWithTitle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import React from "react";

const backgroundColors: Record<Tag, string> = {
  cases: "rgba(0, 31, 43, 0.1)",
  blog: "transparent",
  journal: "#faf5ef",
};

export const TagSection = ({
  label,
  posts,
  tag,
  issues,
}: {
  tag: Tag;
  label: string;
  posts: Post[];
  issues: Post[];
}) => {
  if (!posts.length) return null;
  const latestIssue = issues[0];
  const previousIssue = issues[1];
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 24px;
      `}
    >
      <section
        css={css`
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 8px 12px 24px;
          width: 100%;
          overflow: hidden;
          background-color: ${backgroundColors[tag]};
        `}
      >
        {tag === "journal" && latestIssue ? (
          <Link
            to={`/frj/issue/${latestIssue.slug}`}
            css={css`
              text-decoration: none;
              :hover {
                text-decoration: underline;
              }
            `}
          >
            <FrjLogoWithTitle
              featureImage={latestIssue.feature_image ?? undefined}
              title={latestIssue.title}
            />
          </Link>
        ) : (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            <h2
              css={css`
                font-family: Spectral;
                font-size: 17px;
                font-weight: bold;
                font-stretch: normal;
                font-style: normal;
                line-height: 1.82;
                letter-spacing: normal;
                color: rgba(18, 23, 42, 0.6);
              `}
            >
              <Link
                to={`/frj/tag/${tag}`}
                css={css`
                  color: inherit;
                  text-decoration: none;
                  &:hover {
                    text-decoration: underline;
                  }
                `}
              >
                {label}
              </Link>
            </h2>
            <div
              css={css`
                height: 1px;
                flex-grow: 1;
                border: solid 1px rgba(0, 31, 43, 0.1);
              `}
            ></div>
          </div>
        )}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 8px 0 24px;
          `}
        >
          {posts.map((post, index) => (
            <React.Fragment key={post.url}>
              <ArticleCard key={post.url} post={post} tagGroup={tag} />
              {index !== posts.length - 1 && (
                <div
                  css={css`
                    height: 1px;
                    border: solid 1px rgba(0, 31, 43, 0.1);
                  `}
                ></div>
              )}
            </React.Fragment>
          ))}
        </div>
        {tag === "journal" && latestIssue ? (
          <>
            <div
              css={css`
                height: 1px;
                flex-grow: 1;
                border: solid 1px rgba(0, 31, 43, 0.1);
              `}
            ></div>
            <Link
              to={`/frj/issue/${latestIssue.slug}`}
              css={css`
                display: flex;
                align-items: center;
                align-self: flex-end;
                gap: 4px;

                font-family: Spectral;
                font-size: 16px;
                font-weight: 600;
                text-decoration: none;
                line-height: 1.19;
                letter-spacing: -0.16px;
                color: #1a1f4c;
                :hover {
                  text-decoration: underline;
                }
              `}
            >
              View full issue{" "}
              <ArrowForwardIcon
                css={css`
                  width: 20px;
                `}
              />
            </Link>
          </>
        ) : null}
      </section>
      {tag === "journal" && previousIssue && (
        <section
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            padding: 8px 12px 24px;
            width: 100%;
            overflow: hidden;
            background-color: ${backgroundColors[tag]};
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            <h2
              css={css`
                font-family: Spectral;
                font-size: 17px;
                font-weight: bold;
                font-stretch: normal;
                font-style: normal;
                line-height: 1.82;
                letter-spacing: normal;
                color: rgba(18, 23, 42, 0.6);
              `}
            >
              <div
                css={css`
                  color: inherit;
                  text-decoration: none;
                `}
              >
                PREVIOUS ISSUE
              </div>
            </h2>
            <div
              css={css`
                height: 1px;
                flex-grow: 1;
                border: solid 1px rgba(0, 31, 43, 0.1);
              `}
            ></div>
          </div>
          <Link
            to={`/frj/issue/${previousIssue.slug}`}
            css={css`
              text-decoration: none;
              :hover {
                text-decoration: underline;
              }
            `}
          >
            <FrjLogoWithTitle
              featureImage={previousIssue.feature_image ?? undefined}
              title={previousIssue.title}
            />
          </Link>
          <div
            css={css`
              height: 1px;
              margin-top: 12px;
              flex-grow: 1;
              border: solid 1px rgba(0, 31, 43, 0.1);
            `}
          ></div>
          <Link
            to={`/frj/tag/issues`}
            css={css`
              display: flex;
              align-items: center;
              align-self: flex-end;
              gap: 4px;

              font-family: Spectral;
              font-size: 16px;
              font-weight: 600;
              text-decoration: none;
              line-height: 1.19;
              letter-spacing: -0.16px;
              color: #1a1f4c;
              :hover {
                text-decoration: underline;
              }
            `}
          >
            View older issues{" "}
            <ArrowForwardIcon
              css={css`
                width: 20px;
              `}
            />
          </Link>
        </section>
      )}
    </div>
  );
};
